import { WidgetType } from '@fruitful/types';

import type { ChatMessage, TextContent, UIContent } from '../types/ChatTypes';
import type { MastraThreadMessage } from '../types/MastraThreadMessage';
import { isRecord } from './parsingHelpers';
import { reorderAssistantContentParts } from './reorderAssistantContentParts';
import {
    parseSpendGoalsConfirmedMessage,
    SPEND_GOALS_CONFIRMED_PREFIX,
    type SpendGoalsConfirmedPayload,
} from './spendGoalsMessages';

interface MessagePart {
    type?: string;
    text?: string;
    data?: {
        widget?: string;
        props?: Record<string, unknown> | null;
    };
}

interface MastraMessageContentV2 {
    format?: number;
    parts?: unknown;
    content?: string;
}

const isMessagePart = (value: unknown): value is MessagePart =>
    isRecord(value) && typeof value.type === 'string';

const isMastraMessageContentV2 = (
    value: unknown,
): value is MastraMessageContentV2 =>
    isRecord(value) && (value.format === 2 || Array.isArray(value.parts));

const shouldHideUserMessage = (text: string): boolean => {
    const trimmed = text.trim();
    return (
        trimmed === 'start' || trimmed.startsWith(SPEND_GOALS_CONFIRMED_PREFIX)
    );
};

const extractTextFromParts = (parts: MessagePart[]): string =>
    parts
        .filter((part) => part.type === 'text' && typeof part.text === 'string')
        .map((part) => part.text ?? '')
        .join('');

const extractTextFromContent = (content: unknown): string => {
    if (typeof content === 'string') {
        return content;
    }

    if (isMastraMessageContentV2(content)) {
        if (typeof content.content === 'string') {
            return content.content;
        }

        if (Array.isArray(content.parts)) {
            return extractTextFromParts(content.parts.filter(isMessagePart));
        }
    }

    if (Array.isArray(content)) {
        return extractTextFromParts(content.filter(isMessagePart));
    }

    return '';
};

const extractParts = (message: MastraThreadMessage): MessagePart[] => {
    if (Array.isArray(message.parts)) {
        return message.parts.filter(isMessagePart);
    }

    if (isMastraMessageContentV2(message.content)) {
        if (Array.isArray(message.content.parts)) {
            return message.content.parts.filter(isMessagePart);
        }
    }

    if (Array.isArray(message.content)) {
        return message.content.filter(isMessagePart);
    }

    return [];
};

const extractMessageText = (message: MastraThreadMessage): string => {
    const parts = extractParts(message);
    return (
        extractTextFromParts(parts) || extractTextFromContent(message.content)
    );
};

const toUiContent = (part: MessagePart): UIContent | null =>
    part.type === 'data-ui' && part.data?.widget != null
        ? {
              type: 'ui',
              widget: part.data.widget as WidgetType,
              props: part.data.props ?? null,
          }
        : null;

const extractUiContentFromParts = (parts: MessagePart[]): UIContent[] =>
    parts.map(toUiContent).filter((part): part is UIContent => part != null);

const extractFirstUiContentFromMessage = (
    message: MastraThreadMessage,
): UIContent | null =>
    extractUiContentFromParts(extractParts(message))[0] ?? null;

const buildSpendGoalsConfirmationMap = (
    messages: MastraThreadMessage[],
): Map<string, SpendGoalsConfirmedPayload> => {
    const confirmations = new Map<string, SpendGoalsConfirmedPayload>();

    for (let i = 0; i < messages.length; i++) {
        const message = messages[i];
        if (message?.role !== 'user') {
            continue;
        }

        const confirmation = parseSpendGoalsConfirmedMessage(
            extractMessageText(message),
        );
        if (confirmation == null) {
            continue;
        }

        for (let j = i - 1; j >= 0; j--) {
            const priorMessage = messages[j];
            if (priorMessage == null) {
                continue;
            }

            const uiContent = extractFirstUiContentFromMessage(priorMessage);
            if (uiContent?.widget === WidgetType.SpendGoals) {
                confirmations.set(priorMessage.id, confirmation);
                break;
            }
        }
    }

    return confirmations;
};

const applySpendGoalsConfirmation = (
    uiContent: UIContent,
    confirmation: SpendGoalsConfirmedPayload | undefined,
): UIContent => {
    if (confirmation == null || uiContent.widget !== WidgetType.SpendGoals) {
        return uiContent;
    }

    return {
        ...uiContent,
        props: {
            ...(uiContent.props ?? {}),
            confirmedMonthlySpendCents: confirmation.monthlySpendCents,
            confirmedMonthlyGoalsSpendCents:
                confirmation.monthlyGoalsSpendCents,
            isConfirmed: true,
        },
    };
};

const toChatContent = (
    message: MastraThreadMessage,
    spendGoalsConfirmations: Map<string, SpendGoalsConfirmedPayload>,
): Array<TextContent | UIContent> => {
    const parts = extractParts(message);
    const content: Array<TextContent | UIContent> = [];

    for (const part of parts) {
        if (part.type === 'text' && typeof part.text === 'string') {
            if (part.text.trim() !== '') {
                const previousPart = content[content.length - 1];
                if (previousPart?.type === 'text') {
                    previousPart.text += part.text;
                } else {
                    content.push({ type: 'text', text: part.text });
                }
            }
            continue;
        }

        const uiContent = toUiContent(part);
        if (uiContent != null) {
            content.push(
                applySpendGoalsConfirmation(
                    uiContent,
                    spendGoalsConfirmations.get(message.id),
                ),
            );
        }
    }

    if (!content.some((part) => part.type === 'text')) {
        const fallbackText = extractTextFromContent(message.content);
        if (fallbackText.trim() !== '') {
            content.unshift({ type: 'text', text: fallbackText });
        }
    }

    if (message.role === 'assistant') {
        return reorderAssistantContentParts(content);
    }

    return content;
};

export const convertThreadMessagesToChatMessages = (
    messages: MastraThreadMessage[],
): ChatMessage[] => {
    const spendGoalsConfirmations = buildSpendGoalsConfirmationMap(messages);
    const chatMessages: ChatMessage[] = [];

    for (const message of messages) {
        if (message.role !== 'user' && message.role !== 'assistant') {
            continue;
        }

        const content = toChatContent(message, spendGoalsConfirmations);
        if (content.length === 0) {
            continue;
        }

        const textContent = content
            .filter((part) => part.type === 'text')
            .map((part) => part.text)
            .join('');

        if (message.role === 'user' && shouldHideUserMessage(textContent)) {
            continue;
        }

        chatMessages.push({
            id: message.id,
            role: message.role,
            content,
            createdAt:
                message.createdAt != null
                    ? new Date(message.createdAt)
                    : new Date(),
        });
    }

    chatMessages.sort((a, b) => {
        const timeDiff = a.createdAt.getTime() - b.createdAt.getTime();
        if (timeDiff !== 0) return timeDiff;
        if (a.role === 'user' && b.role === 'assistant') return -1;
        if (a.role === 'assistant' && b.role === 'user') return 1;
        return 0;
    });

    return chatMessages;
};
